/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from 'react';
import { Calendar, Users, DollarSign, Mail, Plus, Search, Edit2, Trash2, X, Check, LogOut, Coffee, Heart } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key';
const supabase = createClient(supabaseUrl, supabaseKey);

const EntertainCMS = () => {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('dashboard');
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);

  // Check authentication status
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load bookings and analytics
  useEffect(() => {
    if (user) {
      loadBookings();
      loadAnalytics();
    }
  }, [user]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('event_date', { ascending: true });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      alert('Error loading bookings. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('analytics_events')
        .select('*')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;

      // Process analytics data
      const pageViews = data.filter(e => e.event_name === 'page_view').length;
      const bookingSubmissions = data.filter(e => e.event_name === 'booking_submitted_success').length;
      const formStarts = data.filter(e => e.event_name === 'form_field_interaction').length;
      
      // Event type popularity
      const eventTypeClicks = data
        .filter(e => e.event_name === 'event_type_selected')
        .reduce((acc, e) => {
          const type = e.event_data?.event_type;
          if (type) acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});

      // Daily views (last 7 days)
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const count = data.filter(e => 
          e.created_at.startsWith(dateStr) && e.event_name === 'page_view'
        ).length;
        last7Days.push({ date: dateStr, views: count });
      }

      setAnalytics({
        pageViews,
        bookingSubmissions,
        conversionRate: pageViews > 0 ? ((bookingSubmissions / pageViews) * 100).toFixed(1) : 0,
        formStarts,
        eventTypePopularity: Object.entries(eventTypeClicks)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 5),
        dailyViews: last7Days,
        totalEvents: data.length
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleLogin = async () => {
    try {
      const { data, error } = isSignUp
        ? await supabase.auth.signUp({ email: loginEmail, password: loginPassword })
        : await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });

      if (error) throw error;
      
      if (isSignUp) {
        alert('Check your email to confirm your account!');
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const saveBooking = async (formData) => {
    try {
      const bookingData = {
        client_name: formData.clientName,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.eventDate,
        event_type: formData.eventType,
        venue: formData.venue,
        fee: formData.fee || 0,
        status: formData.status,
        notes: formData.notes,
        user_id: user.id
      };

      if (formData.id) {
        const { error } = await supabase
          .from('bookings')
          .update(bookingData)
          .eq('id', formData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('bookings')
          .insert([bookingData]);
        if (error) throw error;
      }

      await loadBookings();
    } catch (error) {
      console.error('Error saving booking:', error);
      alert('Error saving booking: ' + error.message);
    }
  };

  const deleteBooking = async (id) => {
    if (!confirm('Delete this booking?')) return;
    
    try {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Error deleting booking: ' + error.message);
    }
  };

  const stats = {
    totalBookings: bookings.length,
    confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
    pendingBookings: bookings.filter(b => b.status === 'pending').length,
    totalRevenue: bookings.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + (b.fee || 0), 0)
  };

  const filteredBookings = bookings.filter(booking =>
    booking.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.event_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Login Screen
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #9333ea, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '48px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{ fontSize: '36px', fontWeight: 'bold', background: 'linear-gradient(to right, #9333ea, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '8px' }}>
              🎪 EntertainCMS
            </h1>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>Booking management for the spicy-minded</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Email</label>
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="your@email.com"
              style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Password</label>
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="••••••••"
              style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px' }}
            />
          </div>

          <button
            onClick={handleLogin}
            style={{
              width: '100%',
              padding: '14px',
              background: 'linear-gradient(to right, #9333ea, #ec4899)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginBottom: '12px'
            }}
          >
            {isSignUp ? 'Sign Up' : 'Login'}
          </button>

          <button
            onClick={() => setIsSignUp(!isSignUp)}
            style={{
              width: '100%',
              padding: '12px',
              background: 'none',
              color: '#9333ea',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            {isSignUp ? 'Already have an account? Login' : 'Need an account? Sign up'}
          </button>

          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Made with ☕ by Chris P Tee</p>
            <a href="https://buymeacoffee.com/chrispteemagician" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#9333ea', fontSize: '14px', textDecoration: 'none' }}>
              <Coffee size={16} />
              Support with a coffee
            </a>
          </div>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '2px solid #f3f4f6'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <div style={{
          background: `${color}20`,
          padding: '10px',
          borderRadius: '8px'
        }}>
          <Icon size={24} color={color} />
        </div>
        <span style={{ color: '#6b7280', fontSize: '14px' }}>{label}</span>
      </div>
      <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827' }}>
        {value}
      </div>
    </div>
  );

  const BookingForm = ({ booking, onClose, onSave }) => {
    const [formData, setFormData] = useState(booking || {
      clientName: '',
      email: '',
      phone: '',
      eventDate: '',
      eventType: '',
      venue: '',
      fee: '',
      status: 'pending',
      notes: ''
    });

    const handleSave = () => {
      if (!formData.clientName || !formData.email || !formData.eventDate || !formData.eventType) {
        alert('Please fill in all required fields');
        return;
      }
      onSave(formData);
      onClose();
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {booking ? 'Edit Booking' : 'New Booking'}
            </h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Client Name *</label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Event Date *</label>
                <input
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Event Type *</label>
                <select
                  value={formData.eventType}
                  onChange={(e) => setFormData({...formData, eventType: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                >
                  <option value="">Select type...</option>
                  <option value="Birthday Party">Birthday Party</option>
                  <option value="Wedding">Wedding</option>
                  <option value="Corporate Event">Corporate Event</option>
                  <option value="School Event">School Event</option>
                  <option value="Community Event">Community Event</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Venue</label>
              <input
                type="text"
                value={formData.venue}
                onChange={(e) => setFormData({...formData, venue: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Fee (£)</label>
                <input
                  type="number"
                  value={formData.fee}
                  onChange={(e) => setFormData({...formData, fee: parseFloat(e.target.value) || ''})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <button
              onClick={handleSave}
              style={{
                background: 'linear-gradient(to right, #9333ea, #ec4899)',
                color: 'white',
                padding: '14px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '8px'
              }}
            >
              {booking ? 'Update Booking' : 'Create Booking'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(to right, #9333ea, #ec4899, #f97316)',
        padding: '24px',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
              🎪 EntertainCMS
            </h1>
            <p style={{ opacity: 0.9 }}>Booking Management</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <a href="https://buymeacoffee.com/chrispteemagician" target="_blank" rel="noopener noreferrer" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255,255,255,0.2)',
              padding: '8px 16px',
              borderRadius: '8px',
              color: 'white',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              <Coffee size={16} />
              Buy me a coffee
            </a>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ borderBottom: '2px solid #e5e7eb', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 24px', display: 'flex', gap: '24px' }}>
          {['dashboard', 'bookings', 'analytics', 'calendar'].map(tab => (
            <button
              key={tab}
              onClick={() => setView(tab)}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px 16px',
                fontSize: '16px',
                fontWeight: view === tab ? 'bold' : 'normal',
                color: view === tab ? '#9333ea' : '#6b7280',
                cursor: 'pointer',
                borderBottom: view === tab ? '3px solid #9333ea' : 'none',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px', color: '#9ca3af' }}>
            Loading bookings...
          </div>
        ) : (
          <>
            {view === 'dashboard' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                  <StatCard icon={Calendar} label="Total Bookings" value={stats.totalBookings} color="#9333ea" />
                  <StatCard icon={Check} label="Confirmed" value={stats.confirmedBookings} color="#22c55e" />
                  <StatCard icon={Mail} label="Pending" value={stats.pendingBookings} color="#f59e0b" />
                  <StatCard icon={DollarSign} label="Revenue" value={`£${stats.totalRevenue}`} color="#3b82f6" />
                </div>

                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>Upcoming Events</h2>
                  {bookings.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>
                      <p>No bookings yet. Click "New Booking" to add one!</p>
                    </div>
                  ) : (
                    bookings.slice(0, 5).map(booking => (
                      <div 
                        key={booking.id}
                        onClick={() => {
                          setEditingBooking({
                            id: booking.id,
                            clientName: booking.client_name,
                            email: booking.email,
                            phone: booking.phone,
                            eventDate: booking.event_date,
                            eventType: booking.event_type,
                            venue: booking.venue,
                            fee: booking.fee,
                            status: booking.status,
                            notes: booking.notes
                          });
                          setShowBookingForm(true);
                        }}
                        style={{
                          padding: '16px',
                          borderBottom: '1px solid #e5e7eb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{booking.client_name}</div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {booking.event_type} • {new Date(booking.event_date).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          background: booking.status === 'confirmed' ? '#dcfce7' : '#fef3c7',
                          color: booking.status === 'confirmed' ? '#166534' : '#92400e'
                        }}>
                          {booking.status}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {view === 'bookings' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                    <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                      type="text"
                      placeholder="Search bookings..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 12px 12px 40px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => setShowBookingForm(true)}
                    style={{
                      background: 'linear-gradient(to right, #9333ea, #ec4899)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Plus size={20} />
                    New Booking
                  </button>
                </div>

                {bookings.length === 0 ? (
                  <div style={{ background: 'white', borderRadius: '12px', padding: '64px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Calendar size={64} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>No bookings yet</h3>
                    <p style={{ color: '#6b7280', marginBottom: '24px' }}>Get started by adding your first booking</p>
                    <button
                      onClick={() => setShowBookingForm(true)}
style={{
                        background: 'linear-gradient(to right, #9333ea, #ec4899)',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Create First Booking
                    </button>
                  </div>
                ) : (
                  <div style={{ background: 'white', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Client</th>
                          <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Event</th>
                          <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                          <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Fee</th>
                          <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                          <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBookings.map(booking => (
                          <tr key={booking.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '16px' }}>
                              <div style={{ fontWeight: '600' }}>{booking.client_name}</div>
                              <div style={{ fontSize: '14px', color: '#6b7280' }}>{booking.email}</div>
                            </td>
                            <td style={{ padding: '16px' }}>{booking.event_type}</td>
                            <td style={{ padding: '16px' }}>{new Date(booking.event_date).toLocaleDateString()}</td>
                            <td style={{ padding: '16px', fontWeight: 'bold' }}>£{booking.fee || 0}</td>
                            <td style={{ padding: '16px' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                background: booking.status === 'confirmed' ? '#dcfce7' : '#fef3c7',
                                color: booking.status === 'confirmed' ? '#166534' : '#92400e'
                              }}>
                                {booking.status}
                              </span>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => {
                                    setEditingBooking({
                                      id: booking.id,
                                      clientName: booking.client_name,
                                      email: booking.email,
                                      phone: booking.phone,
                                      eventDate: booking.event_date,
                                      eventType: booking.event_type,
                                      venue: booking.venue,
                                      fee: booking.fee,
                                      status: booking.status,
                                      notes: booking.notes
                                    });
                                    setShowBookingForm(true);
                                  }}
                                  style={{
                                    padding: '8px',
                                    background: '#eff6ff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Edit2 size={16} color="#3b82f6" />
                                </button>
                                <button
                                  onClick={() => deleteBooking(booking.id)}
                                  style={{
                                    padding: '8px',
                                    background: '#fef2f2',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Trash2 size={16} color="#ef4444" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {view === 'analytics' && analytics && (
              <>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
                  📊 Analytics & Insights
                </h2>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>👁️ Page Views</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9333ea' }}>{analytics.pageViews}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Last 30 days</div>
                  </div>

                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>📝 Form Submissions</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#22c55e' }}>{analytics.bookingSubmissions}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Successful bookings</div>
                  </div>

                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>📈 Conversion Rate</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>{analytics.conversionRate}%</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Views to bookings</div>
                  </div>

                  <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>🎯 Form Interactions</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{analytics.formStarts}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Field interactions</div>
                  </div>
                </div>

                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>📅 Daily Page Views (Last 7 Days)</h3>
                  <div style={{ display: 'flex', alignItems: 'end', gap: '12px', height: '200px' }}>
                    {analytics.dailyViews.map((day, idx) => {
                      const maxViews = Math.max(...analytics.dailyViews.map(d => d.views), 1);
                      const height = (day.views / maxViews) * 160;
                      return (
                        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#9333ea' }}>{day.views}</div>
                          <div style={{
                            width: '100%',
                            height: `${height}px`,
                            background: 'linear-gradient(to top, #9333ea, #ec4899)',
                            borderRadius: '8px 8px 0 0',
                            minHeight: '4px'
                          }} />
                          <div style={{ fontSize: '10px', color: '#6b7280', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                            {new Date(day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>🎯 Most Popular Event Types</h3>
                  {analytics.eventTypePopularity.length > 0 ? (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {analytics.eventTypePopularity.map(([type, count], idx) => {
                        const maxCount = analytics.eventTypePopularity[0][1];
                        const width = (count / maxCount) * 100;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', minWidth: '150px', color: '#374151' }}>{type}</div>
                            <div style={{ flex: 1, height: '24px', background: '#f3f4f6', borderRadius: '4px', position: 'relative' }}>
                              <div style={{ width: `${width}%`, height: '24px', background: 'linear-gradient(to right, #9333ea, #ec4899)', borderRadius: '4px' }} />
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#9333ea', minWidth: '40px' }}>{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#9ca3af', textAlign: 'center', padding: '24px' }}>No event type data yet</p>
                  )}
                </div>
              </>
            )}

            {view === 'calendar' && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                <Calendar size={64} color="#9333ea" style={{ margin: '32px auto' }} />
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Calendar View</h2>
                <p style={{ color: '#6b7280' }}>Interactive calendar coming soon!</p>
              </div>
            )}
          </>
        )}
      </div>

      {showBookingForm && (
        <BookingForm
          booking={editingBooking}
          onClose={() => {
            setShowBookingForm(false);
            setEditingBooking(null);
          }}
          onSave={(formData) => {
            saveBooking(formData);
            setEditingBooking(null);
          }}
        />
      )}
    </div>
  );
};

export default EntertainCMS;